import React from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { fourTheoryLocales } from '../../locales/firstScreen';

import { GlobalFonts } from '../../styles/GlobalFonts';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  height: min(110px, 15vh);
  margin: 50px 0 30px;
  padding: min(13px, 2vh);
  border-radius: 20px;
  justify-content: center;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #0180ff;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: clamp(40px, 5vw, 34px);
  font-weight: 600;
  margin-bottom: min(8px, 1.2vh);
  margin-top: min(1px, 0.2vh);
  line-height: 100%;
`;

const TheoryButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;
const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  position: relative;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(26px, 5vw, 26px);
  font-weight: 600;
  line-height: 90%;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  white-space: pre-line;
`;
const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  margin: 20px 0;
  line-height: 145%;
  letter-spacing: -0.01em;
  white-space: pre-line;
`;

const ScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;

  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding: 0 10px;
  padding-bottom: 100px;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex: 1;
  margin: min(20px, 3vh) auto;
  padding: 0 37px;
  box-sizing: border-box;
`;

const StyledImage = styled.img`
  width: 100%;
  max-width: 500px;
  height: auto;
  object-fit: contain;
  margin: auto;
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

const FourTheory1: React.FC<{
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  toHome?: () => void;
}> = ({ onSwipeLeft, onSwipeRight, toHome }) => {
  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = fourTheoryLocales[lang][1];

  return (
    <div {...handlers}>
      <GlobalFonts />
      <Container>
        <ScrollContainer>
          <HomeButton onClick={toHome}>
            <HomeButtonIcon src="/assets/homeIcon.svg" />
          </HomeButton>
          <Header>
            <Title>{t.title}</Title>
            <TheoryButton>{t.theory}</TheoryButton>
          </Header>
          <QuestionBlock>
            <QuestionText>{t.question}</QuestionText>
          </QuestionBlock>
          <Description>{t.description}</Description>
          <ImageContainer>
            <StyledImage src="/assets/Farm1.svg" alt="Screen illustration" />
          </ImageContainer>
          <Footer
            textLeft={t.back}
            textRight={t.next}
            onSwipeLeft={onSwipeLeft}
            onSwipeRight={onSwipeRight}
          ></Footer>
        </ScrollContainer>
      </Container>
    </div>
  );
};

export default FourTheory1;
